import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { roundNumber } from '@utils/miscUtils'
import { soundManager } from '@core/audio/sounds/soundManager'
import { GAME_STATUS } from '@sockets/socketEvents'
import CrashRoundFairness from './CrashRoundFairness'

// Shared across layouts. `variant` only switches the CSS skin (normal = colored
// background chips; mini = colored text) — the data, level mapping and entrance
// animation are identical, so the logic lives in one place.

const MULTIPLIER_RANGES = [
  { min: 1, max: 1.5, className: 'level-1' },
  { min: 1.5, max: 2, className: 'level-2' },
  { min: 2, max: 5, className: 'level-3' },
  { min: 5, max: 10, className: 'level-4' },
  { min: 10, max: 25, className: 'level-5' },
  { min: 25, max: 50, className: 'level-6' },
  { min: 50, max: 100, className: 'level-7' },
  { min: 100, max: Infinity, className: 'level-8' },
]

// Entrance timing for the newest chip (index 0). The chip grows from a
// collapsed box to its natural width — that width growth is what pushes the
// existing chips to the right — while it simultaneously zooms up from scale(0).
const ENTER_DURATION = 450
const ENTER_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)'

// A stable identity per crash record so React keeps existing chips' DOM nodes
// and mounts a fresh node for the newest one, and so we can detect when a new
// round has been prepended.
const getEntryId = (entry, index) => entry?.roundId ?? entry?.createdAt ?? `idx-${index}`

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Which input opened the card is decided PER INTERACTION from the pointer's own
// type, not from a device-wide media query. A mouse gets hover; a finger or pen
// gets tap-to-toggle. Hybrid devices (touch laptops) report `hover: hover` yet
// still receive taps, so a device-level guess left touch users unable to dismiss:
// the click closed the card and the lingering hover reopened it immediately.
const isMouse = (event) => event.pointerType === 'mouse'

// Grace period when leaving a chip, so the pointer can travel into the card
// without it closing underneath.
const CLOSE_DELAY = 140

const LastCrashMultipliers = ({ variant = 'normal' }) => {
  const { lastCrashMultipliers = [] } = useSelector((state) => state.events)
  const liveRound = useSelector((state) => state.events.round)

  const scrollRef = useRef(null)
  // The newest RESULT chip, which the entrance animation grows. Held as a ref
  // rather than read as `container.firstElementChild` because the pending-round
  // tile now occupies that position.
  const headChipRef = useRef(null)
  const prevHeadIdRef = useRef(null)
  const cleanupRef = useRef(null)

  // The in-flight round, shown as a "?" tile once its commitment is published.
  // The server attaches serverSeedHash at round creation — before betting opens —
  // so the tile appears exactly when the commitment becomes public, and retires
  // when the round ENDS and takes its place in the results list as a multiplier.
  const pendingRound =
    liveRound?.roundId && liveRound.serverSeedHash && liveRound.status !== GAME_STATUS.ENDED
      ? {
          roundId: liveRound.roundId,
          serverSeedHash: liveRound.serverSeedHash,
          status: liveRound.status,
        }
      : null

  // { roundId, el, headIdAtOpen } — the chip whose fairness card is open. `el` is
  // the anchor the card positions against.
  const [openChip, setOpenChip] = useState(null)
  const closeTimerRef = useRef(null)

  const headId = lastCrashMultipliers.length ? getEntryId(lastCrashMultipliers[0], 0) : null

  // A new round prepends a chip and animates the row, which would leave the card
  // anchored to a moved element. Comparing against the head at open time retires
  // the card by derivation — no effect, no cascading render. The player can
  // reopen any chip.
  const activeChip = openChip?.headIdAtOpen === headId ? openChip : null

  const cancelClose = useCallback(() => clearTimeout(closeTimerRef.current), [])

  const close = useCallback(() => {
    cancelClose()
    setOpenChip(null)
  }, [cancelClose])

  const closeSoon = useCallback(() => {
    cancelClose()
    closeTimerRef.current = setTimeout(() => setOpenChip(null), CLOSE_DELAY)
  }, [cancelClose])

  useEffect(() => () => clearTimeout(closeTimerRef.current), [])

  const openFor = useCallback(
    (entry, el, currentHeadId) => {
      cancelClose()
      if (!entry?.roundId) return
      setOpenChip({ roundId: entry.roundId, el, headIdAtOpen: currentHeadId })
    },
    [cancelClose]
  )

  const toggleFor = useCallback(
    (entry, el, currentHeadId) => {
      soundManager.play('CLICK')
      if (activeChip?.roundId === entry.roundId) close()
      else openFor(entry, el, currentHeadId)
    },
    [activeChip, close, openFor]
  )

  // Touch / pen only. Tapping the open chip again dismisses it, which is the
  // primary close gesture on a touchscreen. Mouse input is left to hover so a
  // click cannot fight the hover that is still active.
  //
  // These take the entry as an argument rather than being curried per chip: a
  // curried factory has to be CALLED during render, and the lint rule correctly
  // objects because the closure it returns reaches a ref.
  const handleChipPointerDown = (event, entry) => {
    if (!entry?.roundId || isMouse(event)) return
    toggleFor(entry, event.currentTarget, headId)
  }

  const handleChipKeyDown = (event, entry) => {
    if (!entry?.roundId) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    toggleFor(entry, event.currentTarget, headId)
  }

  // Dismiss on any interaction outside the chip and the card — the touch path's
  // primary close gesture.
  useEffect(() => {
    if (!activeChip) return undefined

    const onPointerDown = (event) => {
      if (activeChip.el?.contains(event.target)) return
      if (event.target.closest?.('.crash-fairness')) return
      close()
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [activeChip, close])

  useLayoutEffect(() => {
    const prevHeadId = prevHeadIdRef.current
    prevHeadIdRef.current = headId

    // Skip the very first render (initial socket hydration), empty lists, and
    // updates that didn't actually prepend a new round.
    if (prevHeadId === null || headId === null || headId === prevHeadId) return
    if (prefersReducedMotion()) return

    const container = scrollRef.current
    // The newest result chip — NOT firstElementChild, which is the pending-round
    // "?" tile whenever a round is in flight. Animating that instead would grow
    // the wrong element and leave the new result appearing without motion.
    const el = headChipRef.current
    if (!container || !el) return

    // Finalize any entrance still in flight (rapid rounds) before starting a new one.
    if (cleanupRef.current) cleanupRef.current()

    const styles = window.getComputedStyle(container)
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0
    const naturalWidth = el.offsetWidth // border-box outer width at natural size

    // FIRST — collapse the newcomer so the row looks exactly like it did before
    // the update (no visible jump), anchored to its left edge.
    el.style.boxSizing = 'border-box'
    el.style.overflow = 'hidden'
    el.style.whiteSpace = 'nowrap'
    el.style.transformOrigin = 'left center'
    el.style.willChange = 'width, transform, opacity'
    el.style.transition = 'none'
    el.style.width = '0px'
    el.style.marginRight = `-${gap}px` // cancel the flex gap while collapsed
    el.style.transform = 'scale(0.3)'
    el.style.opacity = '0'

    // Commit the collapsed state before we animate to the final one.
    void el.offsetWidth // force reflow

    // PLAY — grow to full width (this slides every later chip to the right) and
    // zoom in from scale(0.3) -> scale(1).
    el.style.transition =
      `width ${ENTER_DURATION}ms ${ENTER_EASING}, ` +
      `margin-right ${ENTER_DURATION}ms ${ENTER_EASING}, ` +
      `transform ${ENTER_DURATION}ms ${ENTER_EASING}, ` +
      `opacity ${Math.round(ENTER_DURATION * 0.6)}ms ease-out`
    el.style.width = `${naturalWidth}px`
    el.style.marginRight = '0px'
    el.style.transform = 'scale(1)'
    el.style.opacity = '1'

    let done = false
    const finish = () => {
      if (done) return
      done = true
      el.removeEventListener('transitionend', onEnd)
      clearTimeout(timer)
      // Return to the natural, CSS-driven layout.
      el.style.transition = ''
      el.style.width = ''
      el.style.marginRight = ''
      el.style.transform = ''
      el.style.opacity = ''
      el.style.boxSizing = ''
      el.style.overflow = ''
      el.style.whiteSpace = ''
      el.style.transformOrigin = ''
      el.style.willChange = ''
      if (cleanupRef.current === finish) cleanupRef.current = null
    }
    const onEnd = (e) => {
      if (e.target === el && e.propertyName === 'width') finish()
    }
    el.addEventListener('transitionend', onEnd)
    const timer = setTimeout(finish, ENTER_DURATION + 120) // safety net
    cleanupRef.current = finish

    return finish
  }, [headId])

  const getMultiplierClass = (multiplier) => {
    return (
      MULTIPLIER_RANGES.find(({ min, max }) => multiplier >= min && multiplier < max)?.className ??
      'level-1'
    )
  }

  return (
    <div className={`crash-multipliers crash-multipliers--${variant}`}>
      {/* Chips stay the DIRECT children of this scroller — the entrance
          animation grows one of them and relies on the flex gap. */}
      <div ref={scrollRef} className="crash-multipliers__scroll no-scrollbar">
        {/* Pending round: its commitment is public but its outcome does not exist
            yet, so it shows "?" rather than a multiplier. Leftmost because this
            row runs newest-first. */}
        {pendingRound && (
          <div
            key="pending"
            className={`crash-multipliers__chip crash-multipliers__chip--pending is-verifiable${
              activeChip?.roundId === pendingRound.roundId ? ' is-open' : ''
            }`}
            role="button"
            tabIndex={0}
            aria-expanded={activeChip?.roundId === pendingRound.roundId}
            aria-label="Current round — show provably fair commitment"
            onPointerDown={(event) => handleChipPointerDown(event, pendingRound)}
            onKeyDown={(event) => handleChipKeyDown(event, pendingRound)}
            onPointerEnter={(event) =>
              isMouse(event) && openFor(pendingRound, event.currentTarget, headId)
            }
            onPointerLeave={(event) => isMouse(event) && closeSoon()}
          >
            ?
          </div>
        )}

        {lastCrashMultipliers.map((multiplier, index) => {
          const MULTIPLIER = roundNumber(multiplier.crashMultiplier)
          const isOpen = Boolean(activeChip?.roundId) && activeChip.roundId === multiplier.roundId
          const isVerifiable = Boolean(multiplier.roundId)

          return (
            <div
              key={getEntryId(multiplier, index)}
              ref={index === 0 ? headChipRef : null}
              className={`crash-multipliers__chip ${getMultiplierClass(MULTIPLIER)}${
                isVerifiable ? ' is-verifiable' : ''
              }${isOpen ? ' is-open' : ''}`}
              // Kept as a div with button semantics: switching the element type
              // would reset padding/font and change the chip's rendered size.
              role={isVerifiable ? 'button' : undefined}
              tabIndex={isVerifiable ? 0 : undefined}
              aria-expanded={isVerifiable ? Boolean(isOpen) : undefined}
              aria-label={isVerifiable ? `${MULTIPLIER} — show provably fair details` : undefined}
              onPointerDown={
                isVerifiable ? (event) => handleChipPointerDown(event, multiplier) : undefined
              }
              onKeyDown={isVerifiable ? (event) => handleChipKeyDown(event, multiplier) : undefined}
              onPointerEnter={
                isVerifiable
                  ? (event) => isMouse(event) && openFor(multiplier, event.currentTarget, headId)
                  : undefined
              }
              onPointerLeave={
                isVerifiable ? (event) => isMouse(event) && closeSoon() : undefined
              }
            >
              {MULTIPLIER}
            </div>
          )
        })}
      </div>

      {activeChip && (
        <CrashRoundFairness
          roundId={activeChip.roundId}
          anchorEl={activeChip.el}
          variant={variant}
          // When the open chip is the in-flight round, the card renders the
          // commitment-only view: there is no seed and no outcome to show yet.
          pending={activeChip.roundId === pendingRound?.roundId ? pendingRound : null}
          onRequestClose={close}
          // Moving the mouse into the card keeps it open so its values can be
          // copied; leaving it starts the close. Touch never triggers these.
          onMouseIn={cancelClose}
          onMouseOut={closeSoon}
        />
      )}
    </div>
  )
}

export default LastCrashMultipliers
